/**
 * MED-1360: the time a date-time field applies always equals the time its box
 * shows.
 *
 * Mantine applies its own internal time on a date pick. A click in the time
 * box, or a mount before the default time changed, leaves that internal time
 * empty or stale, so a pick could save 12:00 AM while the box showed 9:00 AM.
 * The field now decides the time itself: a date pick takes the shown time,
 * and only an edit in the time box changes the time.
 */

/** "HH:mm" in local time, the format Mantine's time box reads. */
export function toTimeString(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** The time the box shows: the field's own time, else the default, else none. */
export function shownTime(value: Date | null, defaultTime: string | undefined): string | undefined {
  return value ? toTimeString(value) : defaultTime;
}

/**
 * The field value after Mantine reports a change.
 * - A time box edit is the user's time, so it stays.
 * - A date pick gets the time the box showed before the pick. With no shown
 *   time (an empty field with no default) Mantine's value stays.
 */
export function dateTimeAfterChange({
  previous,
  next,
  fromTimeBox,
  defaultTime,
  onFirstDatePick,
}: {
  previous: Date | null;
  next: Date | null;
  fromTimeBox: boolean;
  defaultTime: string | undefined;
  /** The value of a date pick into the empty field, when the form sets it. */
  onFirstDatePick?: (picked: Date) => Date;
}): Date | null {
  if (!next || fromTimeBox) return next;
  if (!previous && onFirstDatePick) return onFirstDatePick(next);
  const time = shownTime(previous, defaultTime);
  const match = time?.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return next;
  const result = new Date(next.getTime());
  result.setHours(Number(match[1]), Number(match[2]), 0, 0);
  return result;
}

/**
 * MED-1360: the minDate Mantine gets, the start of the given day. Mantine
 * moves a value before its minDate up to minDate when the picker closes, and
 * limits the time box to minDate's time. With the full start time, a typed
 * end of 1:30 PM next to a 2:00 PM start became 2:00 PM. With the day only,
 * earlier days stay disabled and a typed time stays as typed, so the form's
 * own end error shows instead.
 */
export function calendarMinDate(minDate: Date | undefined): Date | undefined {
  if (!minDate) return undefined;
  const day = new Date(minDate.getTime());
  day.setHours(0, 0, 0, 0);
  return day;
}
